/**
 * MinMaxContextProvider provides context and state management for Min-Max Settings in the inventory module.
 *
 * Features:
 * - Manages state for min-max settings, including pagination, sorting, searching, and loading.
 * - Provides CRUD operations for min-max settings (create, read, update, delete) via API calls.
 * - Handles form data clearing and error management.
 * - Exposes context values and actions to child components via React Context API.
 *
 * Context Values Provided:
 * - minMaxSettings, setMinMaxSettings, initialState: State and setter for the current min-max setting form.
 * - error, isLoading: Error and loading state.
 * - totalElements, currentPage, pageSize, searchQuery, sortBy, sortDir: Pagination, search, and sorting state.
 * - setCurrentPage, setPageSize, setSearchQuery, setSortBy, setSortDir: Setters for pagination, search, and sorting.
 * - getAllMinMaxSettingsAPI: Fetches min-max settings from the API.
 * - createMinMaxSettings: Creates a new min-max setting.
 * - updateMinMaxSettings: Updates an existing min-max setting.
 * - deleteMinMaxSettingsAPI: Deletes a min-max setting.
 * - minMaxSettingsData: Array of min-max settings for display.
 * - clearFormData: Resets the form state.
 *
 * Usage:
 * Wrap your component tree with <MinMaxContextProvider> to provide access to min-max settings context.
 * Use the useMinMaxContext() hook to access context values and actions in child components.
 */

import React, { useCallback, useState } from 'react';
import MinMaxContext from './MinMaxContext';
import minMaxSettingsModel from '@/model/inventory/MinMaxSettings.model';
import MinMaxReportModel from '@/model/inventory/MinMaxReport.model';
import { useLoading } from '@/utils/LoadingUtil';
import {
  getMinMaxSettings,
  postMinMaxSettings,
  putMinMaxSettings,
  deleteMinMaxSettings,
  getAbcClassList,
  getMinMaxReportAPI,
  importForPronto,
} from '@/services/MinMaxService';
import { showAlert, ConfirmAlert } from '@/components';
import { handleApiError } from '@/lib/handleApiError';

const MinMaxContextProvider = ({ children }) => {
  const { minMaxSettings, setMinMaxSettings, initialState } =
    minMaxSettingsModel();
  const {
    minMaxReport,
    setMinMaxReport,
    resetMinMaxReport,
    initialState: minMaxReportInitialState,
  } = MinMaxReportModel();
  const [error, setError] = useState(null);
  const { isLoading, startLoading, stopLoading, setloadingText } = useLoading();
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [minMaxSettingsData, setMinMaxSettingsData] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [abcClassList, setAbcClassList] = useState([]);
  const [mainProductGroupList, setMainProductGroupList] = useState([]);
  const [abcOptions, setAbcOptions] = useState([]);

  //clear form data
  const clearFormData = () => {
    setMinMaxSettings({ ...initialState });
    setError(null);
  };

  // get min and max settings data
  const getAllMinMaxSettingsAPI = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      sortField = sortBy, // Renamed parameter to avoid shadowing
      sortDirection = sortDir, // Renamed parameter to avoid shadowing
    ) => {
      startLoading();
      try {
        const data = await getMinMaxSettings(
          page,
          size,
          search,
          sortField, // Use renamed parameter
          sortDirection, // Use renamed parameter
        );
        setMinMaxSettingsData(
          data?._embedded?.minMaxSettingResponseList || ['No Data Found'],
        );
        setTotalElements(data?.page?.totalElements);
      } catch (error) {
        console.log(error);
        handleApiError(
          'error',
          error.response?.data ||
            'An error occurred while fetching MinMax settings',
          error?.response?.status,
        );
      } finally {
        stopLoading();
      }
    },
    [
      startLoading,
      stopLoading,
      currentPage,
      pageSize,
      searchQuery,
      sortBy,
      sortDir,
    ],
  );

  // create a new min and max settings
  const createMinMaxSettings = useCallback(
    async (minMaxSettings, handleClose) => {
      startLoading();
      try {
        await postMinMaxSettings(minMaxSettings);
        await getAllMinMaxSettingsAPI();
        handleClose();
        showAlert('success', 'Min-Max settings added successfully');
      } catch (error) {
        handleApiError(
          'error',
          error.response?.data ||
            'An error occurred while adding MinMax settings',
          error?.response?.status,
        );
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, getAllMinMaxSettingsAPI],
  );

  const updateMinMaxSettings = useCallback(
    async (id, minMaxSettings, handleClose) => {
      startLoading();
      try {
        await putMinMaxSettings(id, minMaxSettings);
        await getAllMinMaxSettingsAPI();
        handleClose();
        showAlert('success', 'Min-Max settings updated successfully');
      } catch (error) {
        handleApiError(
          'error',
          error.response?.data ||
            'An error occurred while updating MinMax settings',
          error?.response?.status,
        );
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, getAllMinMaxSettingsAPI],
  );

  const deleteMinMaxSettingsAPI = useCallback(
    async (id) => {
      const result = await ConfirmAlert({
        confirmBtnText: 'Delete Min-Max Setting',
      });
      if (!result.isConfirmed) {
        stopLoading();
        showAlert('error', 'Min-Max cancelled');
        return;
      }
      startLoading();
      try {
        await deleteMinMaxSettings(id);
        await getAllMinMaxSettingsAPI();
        showAlert('success', 'Min-Max settings deleted successfully');
      } catch (error) {
        handleApiError('error', error.response.data, error?.response?.status);
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, getAllMinMaxSettingsAPI],
  );

  //Min-Max report

  const getAbcClassListAPI = useCallback(async () => {
    startLoading();
    try {
      const data = await getAbcClassList();
      console.log('ress.....', data);
      setAbcClassList(data);
    } catch (error) {
      handleApiError(
        'error',
        error.response?.data || 'An error occurred while fetching ABC classes',
        error?.response?.status,
      );
      setAbcOptions([]);
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  const importForProntoAPI = useCallback(
    async (payload) => {
      startLoading();
      try {
        const data = await importForPronto(payload);
        downloadFile(data, 'min-max-import-for-pronto.csv');
      } catch (error) {
        handleApiError(
          'error',
          error.response?.data ||
            'An error occurred while fetching ABC classes',
          error?.response?.status,
        );
        setAbcOptions([]);
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  //download file
  const downloadFile = (data, filename) => {
    if (!data || data.length === 0) {
      return;
    }
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    showAlert('success', 'Excel downloaded successfully');
  };

  //download excel file
  const downloadExcel = async (payload) => {
    startLoading();
    try {
      const data = await getMinMaxReportAPI(payload);
      downloadFile(data, 'minmax_report_file.xlsx');
    } catch (error) {
      // showAlert("error", error.message || "An error occurred");
    } finally {
      stopLoading();
    }
  };

  return (
    <MinMaxContext.Provider
      value={{
        minMaxSettings,
        setMinMaxSettings,
        initialState,
        error,
        isLoading,
        totalElements,
        currentPage,
        pageSize,
        searchQuery,
        sortBy,
        sortDir,
        setCurrentPage,
        setPageSize,
        setSearchQuery,
        setSortBy,
        setSortDir,
        getAllMinMaxSettingsAPI,
        createMinMaxSettings,
        updateMinMaxSettings,
        deleteMinMaxSettingsAPI,
        minMaxSettingsData,
        clearFormData,
        getAbcClassListAPI,
        warehouseList,
        abcClassList,
        mainProductGroupList,
        setWarehouseList,
        setAbcClassList,
        setMainProductGroupList,
        abcOptions,
        setAbcOptions,
        minMaxReport,
        setMinMaxReport,
        resetMinMaxReport,
        downloadExcel,
        importForProntoAPI,
      }}>
      {children}
    </MinMaxContext.Provider>
  );
};

export default MinMaxContextProvider;
export const useMinMaxContext = () => React.useContext(MinMaxContext);
